import { CancelToken } from 'axios'

import { portalApi } from '@/axios'

import { Batch, BatchDetail, Cost, MerchantAccountGroup, Transfer } from '@/classes/models'

export default class MerchantAccountGroupService {
  /**
   * Get all merchant account groups.
   *
   * @param {Context} context
   *
   * @return {Promise}
   */
  static getAll (context) {
    const businessId = context?.business?.id || context?.businessId
    const merchantId = context?.merchant?.id || context?.merchantId

    if (!businessId || !merchantId) {
      return Promise.reject(new Error('Invalid parameters: businessId and merchantId are required'))
    }

    const url = this.endpoint(context)

    return portalApi.get(url)
      .then(response => new MerchantAccountGroup(response.data))
      .catch(error => {
        return Promise.reject(error)
      })
  }

  /**
   * Get a merchant account group by account-number.
   *
   * @param {Context} context
   * @param {string} accountNumber
   *
   * @return {Promise}
   */
  static get ({ businessId, merchantId, accountNumber }) {
    return portalApi.get(`/businesses/${businessId}/merchants/${merchantId}/merchant-accountgroups/${accountNumber}`)
      .then(response => response.data)
      .catch(error => Promise.reject(error))
  }

  /**
   * Gets the costs that match the given search criteria, for the AccountNumber belonging to the Business/Merchant specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {TableRequest} parameters
   *
   * @return {Promise}
   */
  static getCosts (context, accountNumber, parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(`${this.endpoint(context)}/${accountNumber}/costs`, {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Set pagination
      response.totalCount = response.numberOfResults

      // Map to Cost
      response.data = response.results.map(batch => {
        return new Cost(batch)
      })

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Gets the transfers that match the given search criteria, for the AccountNumber belonging to the Business/Merchant specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {TableRequest} parameters
   *
   * @return {Promise}
   */
  static getTransfers (context, accountNumber, parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(`${this.endpoint(context)}/${accountNumber}/transfers`, {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Set pagination
      response.totalCount = response.numberOfResults

      // Map to Transfer
      response.data = response.results.map(batch => {
        return new Transfer(batch)
      })

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Gets the transfers report, that match the given search criteria, for the AccountNumber belonging to the Business/Merchant specified
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {object} parameters
   *
   * @return {Promise}
   */
  static getTransfersReports (context, accountNumber, parameters) {
    return portalApi.post(`${this.endpoint(context)}/${accountNumber}/transfers/reports`, null, { params: parameters || null })
  }

  /**
   * Gets the costs reports, that match the given search criteria, for the AccountNumber belonging to the Business/Merchant specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {object} parameters
   *
   * @return {Promise}
   */
  static getCostsReports (context, accountNumber, parameters) {
    return portalApi.post(`${this.endpoint(context)}/${accountNumber}/costs/reports`, null, { params: parameters || null })
  }

  /**
   * Gets the settlement batches that match the given search criteria, for the AccountNumber belonging to the Business/Merchant specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {TableRequest} parameters
   *
   * @return {Promise}
   */
  static getBatches (context, accountNumber, parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(`${this.endpoint(context)}/${accountNumber}/batches`, {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Set pagination
      response.totalCount = response.numberOfResults

      // Map to Batch
      response.data = response.results.map(batch => {
        return new Batch(batch)
      })

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Gets the settlement batches report, that match the given search criteria, for the AccountNumber belonging to the Business/Merchant specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {object} parameters
   *
   * @return {Promise}
   */
  static getBatchesReports (context, accountNumber, parameters) {
    return portalApi.post(`${this.endpoint(context)}/${accountNumber}/batches/reports`, null, { params: parameters || null })
  }

  /**
   * Gets the batch detail statement, for the BatchId for the AccountNumber belonging to the Business/Merchant specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {string} batchId
   * @param {TableRequest} parameters
   *
   * @return {Promise}
   */
  static getBatch (context, accountNumber, batchId, parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(`${this.endpoint(context)}/${accountNumber}/batches/${batchId}`, {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Set pagination
      response.totalCount = response.numberOfResults

      // Map to Batch detail
      response.data = response.results.map(batch => {
        return new BatchDetail(batch)
      })

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Gets the batch detail statement report, for the BatchId for the AccountNumber belonging to the Business/Merchant specified.
   *
   * @param {Context} context
   * @param {string} accountNumber
   * @param {string} batchId
   * @param {object} parameters
   *
   * @return {Promise}
   */
  static getBatchReports (context, accountNumber, batchId, parameters) {
    return portalApi.post(`${this.endpoint(context)}/${accountNumber}/batches/${batchId}/reports`, null, { params: parameters || null })
  }

  /**
   * Endpoint of API.
   *
   * @return {string}
   */
  static endpoint (context) {
    const businessId = context?.business?.id || context?.businessId
    const merchantId = context?.merchant?.id || context?.merchantId

    if (!businessId || !merchantId) {
      throw new Error('Missing businessId or merchantId in context:', context)
    }

    return `/businesses/${businessId}/merchants/${merchantId}/merchant-accountgroups`
  }
}
